import { AxiosRequestConfig, AxiosResponse } from 'axios'
import React from 'react'

import agent from 'agent'
import Button from 'components/common/Button'
import toast from 'lib/toast'
import log from 'log'
import { messageFromResponseError } from 'utils/strings'

export interface TriggerStatus {
  success: boolean
  error: boolean
  loading: boolean
  response: AxiosResponse | Record<string, any>
}

interface ApiTriggerProps {
  as?: keyof JSX.IntrinsicElements | React.ComponentType
  request: AxiosRequestConfig
  onRequest?: (status: TriggerStatus) => void
  onSuccess?: (status: TriggerStatus) => void
  onFailure?: (status: TriggerStatus) => void
  onError?: () => void
  successToastMessage: string
  errorToastMessage: string
  eventProp?: string
  disabled?: boolean
}

const ApiTrigger: React.FC<typeof Button & ApiTriggerProps> = ({
  as: Trigger = Button,
  eventProp = 'onClick',
  request,
  onRequest,
  onSuccess,
  onFailure,
  successToastMessage,
  errorToastMessage,
  disabled = false,
  ...rest
}) => {
  const propsWithHandler = {
    ...rest,
    [eventProp]: async (e) => {
      if (rest[eventProp]) rest[eventProp](e)

      if (onRequest)
        onRequest({
          success: false,
          error: false,
          loading: true,
          response: {}
        })

      let res
      try {
        res = await agent(request)
      } catch (err) {
        log.error(err)

        if (errorToastMessage) {
          toast({
            type: 'error',
            text: messageFromResponseError(err, errorToastMessage)
          })
        }

        if (onFailure)
          onFailure({
            success: false,
            error: true,
            loading: false,
            response: err.response
          })
        return
      }

      if (successToastMessage) {
        toast({
          type: 'success',
          text: successToastMessage
        })
      }

      if (onSuccess)
        onSuccess({
          success: true,
          error: false,
          loading: false,
          response: res
        })
    }
  }

  return <Trigger disabled={disabled} {...propsWithHandler} />
}

export default ApiTrigger
